import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowLeft, Bot, Check, MessageCircle } from 'lucide-react';
import PageScaffold from '../component/pageScaffold/PageScaffold';
import { httpClient } from '../common/httpclient';
import { usePermissions } from '../hooks/usePermissions';
import { useIsMobileShell } from '../hooks/useIsMobileShell';
import { KitButton, KitCard, KitPill } from '../component/common/MobileKit';
import { ProtoIcon } from '../component/common/ProtoIcon';
import { WHATSAPP_CONTACT_URL } from './landing/content';
import { buildPlanTiers, tierHeadline } from '../common/planTiers';
import { planDisplayName, isTrialPlanName } from '../common/planDisplayName';

/**
 * Planos — tela autenticada para o usuário (owner) assinar ou fazer upgrade.
 * Dados: GET /enterprise/plans (catálogo + plano atual) e GET /enterprise/plan-usage (teste).
 * Acesso pelo botão "Ver outros planos" em Minha Conta > Meu plano.
 */

const formatInt = (n) => Number(n || 0).toLocaleString('pt-BR');

const formatMB = (mb) => (mb >= 1024
  ? `${formatInt(Math.round((mb / 1024) * 10) / 10)} GB`
  : `${formatInt(mb)} MB`);

const planCents = (plan) => (Number(plan.value_reais) || 0) * 100 + (Number(plan.value_cents) || 0);

// Só aceita link https; qualquer outra coisa cai no botão "Falar com consultor".
const safePaymentLink = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch (e) {
    return null;
  }
};

const trialStatusLabel = (trial) => {
  if (trial.expired) return 'Período de teste encerrado';
  const days = trial.days_left;
  return `Período de teste — ${days} ${days === 1 ? 'dia restante' : 'dias restantes'}`;
};

// Cartão de plano do shell mobile (`planos.html`): nome 17/700 + chip, preço 28/800, lista com check, botão largo.
function PlanCardMobile({ plan, tier, currentCents, hasPlan }) {
  const link = safePaymentLink(plan.payment_link_url);
  const cents = planCents(plan);
  const isUpgrade = hasPlan && currentCents !== null && cents > currentCents;
  const name = planDisplayName(plan.name);

  const lines = [];
  if (plan.max_users > 0) lines.push(`Até ${formatInt(plan.max_users)} usuários`);
  if (plan.max_data_mb > 0) lines.push(`${formatMB(plan.max_data_mb)} de armazenamento`);
  if (plan.ai_tokens > 0) lines.push(`${formatInt(plan.ai_tokens)} tokens de IA`);
  if (plan.whatsapp_messages > 0) lines.push(`${formatInt(plan.whatsapp_messages)} mensagens de WhatsApp/mês`);
  if (tier?.baseName) lines.push(tierHeadline(tier));
  (tier ? tier.modules : []).forEach((mod) => lines.push(mod.label));

  const actionProps = { block: true, sx: { mt: '12px' }, component: 'a', target: '_blank', rel: 'noopener noreferrer' };

  return (
    <KitCard sx={plan.current ? { border: '2px solid', borderColor: 'primary.main' } : undefined}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <Box sx={{ fontWeight: 700, fontSize: 17 }}>{name}</Box>
        {plan.current && <KitPill tone="info">Plano atual</KitPill>}
      </Box>
      {plan.description && <Box sx={{ fontSize: 12.5, color: 'text.disabled', mt: '4px' }}>{plan.description}</Box>}
      <Box sx={{ m: '6px 0 10px' }}>
        <Box component="span" sx={{ fontWeight: 800, fontSize: 28, letterSpacing: '-.03em' }}>
          R$ {formatInt(plan.value_reais)},{String(plan.value_cents || 0).padStart(2, '0')}
        </Box>
        <Box component="span" sx={{ color: 'text.disabled', fontSize: 12.5 }}>/mês</Box>
      </Box>
      {lines.map((text) => (
        <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: 12.5, m: '6px 0' }}>
          <Box component="span" sx={{ display: 'inline-flex', color: 'success.main' }}><ProtoIcon name="check" size={16} /></Box>
          {text}
        </Box>
      ))}
      {plan.current ? (
        <KitButton block disabled sx={{ mt: '12px' }}>Plano atual</KitButton>
      ) : link ? (
        <KitButton variant="primary" {...actionProps} href={link}>{isUpgrade ? 'Fazer upgrade' : `Escolher ${name}`}</KitButton>
      ) : (
        <KitButton {...actionProps} href={WHATSAPP_CONTACT_URL}>Falar com consultor</KitButton>
      )}
    </KitCard>
  );
}

function PlanCard({ plan, tier, currentCents, hasPlan }) {
  const theme = useTheme();
  const link = safePaymentLink(plan.payment_link_url);
  // Todos os módulos, ou só os extras quando o plano herda de um plano-base (planTiers).
  const modules = tier ? tier.modules : [];
  const cents = planCents(plan);
  const isUpgrade = hasPlan && currentCents !== null && cents > currentCents;

  const features = [];
  if (plan.ai_tokens > 0) features.push({ icon: Bot, text: `${formatInt(plan.ai_tokens)} tokens de IA` });
  if (plan.whatsapp_messages > 0) features.push({ icon: MessageCircle, text: `${formatInt(plan.whatsapp_messages)} mensagens de WhatsApp/mês` });
  if (plan.max_users > 0) features.push({ icon: Check, text: `Até ${formatInt(plan.max_users)} usuários` });
  if (plan.max_data_mb > 0) features.push({ icon: Check, text: `${formatMB(plan.max_data_mb)} de armazenamento` });

  return (
    <Card
      elevation={0}
      sx={{
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: plan.current ? 'primary.main' : 'divider',
        boxShadow: plan.current ? theme.customShadows.md : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 800 }}>{planDisplayName(plan.name)}</Typography>
        {plan.current && <Chip size="small" color="primary" label="Plano atual" />}
      </Box>
      {plan.description && (
        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mt: '4px' }}>{plan.description}</Typography>
      )}
      <Typography sx={{ fontSize: '24px', fontWeight: 800, color: 'primary.main', mt: 1.5 }}>
        R$ {formatInt(plan.value_reais)},{String(plan.value_cents || 0).padStart(2, '0')}{' '}
        <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.disabled' }}>/mês</Typography>
      </Typography>

      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5, display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
        {features.map(({ icon: Icon, text }) => (
          <Box component="li" key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '13px' }}>
            <Icon size={14} strokeWidth={2} />
            {text}
          </Box>
        ))}
        {tier?.baseName && (
          <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '13px', fontWeight: 700 }}>
            <Check size={14} strokeWidth={2} />
            {tierHeadline(tier)}
          </Box>
        )}
        {modules.map((mod) => (
          <Box component="li" key={mod.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: '13px' }}>
            <Box sx={{ mt: '2px', display: 'flex' }}><Check size={14} strokeWidth={2} /></Box>
            <Box>
              {mod.label}
              {mod.description && (
                <Typography component="span" sx={{ display: 'block', fontSize: '11.5px', color: 'text.disabled' }}>
                  {mod.description}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 2 }}>
        {plan.current ? (
          <Button fullWidth variant="outlined" size="small" disabled>Plano atual</Button>
        ) : link ? (
          <Button
            fullWidth
            variant="contained"
            size="small"
            component="a"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {isUpgrade ? 'Fazer upgrade' : 'Assinar'}
          </Button>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            component="a"
            href={WHATSAPP_CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Falar com consultor
          </Button>
        )}
      </Box>
    </Card>
  );
}

export default function Plans() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileShell = useIsMobileShell();
  const { userData, loading } = usePermissions();
  const [plans, setPlans] = useState([]);
  const [planUsage, setPlanUsage] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Assinatura é assunto do owner, como Minha Conta.
  useEffect(() => {
    if (!loading && (!userData || userData.is_owner !== true)) {
      navigate('/home');
    }
  }, [userData, loading, navigate]);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    httpClient.get('/enterprise/plans')
      .then((response) => {
        if (!active) return;
        setPlans(Array.isArray(response.data) ? response.data : []);
        setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('error');
      });
    // O status do teste é complementar: se falhar, a lista de planos segue funcionando.
    httpClient.get('/enterprise/plan-usage')
      .then((response) => {
        if (active) setPlanUsage(response.data);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const currentPlan = plans.find((plan) => plan.current);
  const currentCents = currentPlan ? planCents(currentPlan) : null;
  const trial = planUsage?.has_plan ? planUsage.trial : null;
  // O plano de teste/grátis não vira card: o status do teste fica no banner acima.
  // Filtra antes dos tiers para o "Tudo do plano X, mais:" não citar o Grátis.
  const visiblePlans = plans.filter((plan) => !isTrialPlanName(plan.name));
  const tiers = buildPlanTiers(visiblePlans);

  return (
    <PageScaffold
      links={[]}
      titlePageTarget="Planos"
      loading={false}
      setOpenSnackbar={setOpenSnackbar}
      openSnackbar={openSnackbar}
      severity="error"
      message=""
    >
      <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', pb: '40px' }}>
        {isMobileShell ? (
          // Mensal/Anual do protótipo: a API só tem preço mensal, então "Anual" fica indisponível.
          <Box sx={{ display: 'flex', backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '14px', p: '3px', gap: '2px', mb: '16px' }}>
            <Box component="button" type="button" sx={{ flex: 1, height: 36, borderRadius: '11px', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', border: 0, cursor: 'pointer', backgroundColor: 'background.paper', color: 'primary.main', boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>Mensal</Box>
            <Box component="button" type="button" disabled title="Em breve" sx={{ flex: 1, height: 36, borderRadius: '11px', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', border: 0, background: 'none', color: 'text.secondary', cursor: 'default' }}>Anual · -20%</Box>
          </Box>
        ) : (
          <>
            <Button size="small" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('/myaccount')} sx={{ mb: 1.5 }}>
              Voltar para Minha Conta
            </Button>

            <Typography component="h1" sx={{ fontSize: '19px', fontWeight: 800, mb: '4px' }}>
              Escolha seu plano
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 2 }}>
              Assine ou faça upgrade quando quiser. O pagamento é concluído em uma página segura em nova aba.
            </Typography>
          </>
        )}

        {trial && (
          <Card
            elevation={0}
            sx={{
              p: '16px 20px',
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.secondary.bg}, ${theme.palette.info.bg})`,
            }}
          >
            <Typography sx={{ fontSize: '15px', fontWeight: 800 }}>{trialStatusLabel(trial)}</Typography>
            <Typography sx={{ fontSize: '12px', color: trial.expired ? 'error.main' : 'text.disabled', mt: '4px' }}>
              {trial.expired
                ? 'Seu período de teste terminou. Escolha um plano para continuar usando o sistema. Seus dados foram mantidos e você segue com acesso a Minha Conta.'
                : 'Todas as funcionalidades estão liberadas durante o teste. Assine para continuar depois.'}
            </Typography>
          </Card>
        )}

        {status === 'loading' && (
          <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>Carregando planos...</Typography>
        )}

        {status === 'error' && (
          <Typography sx={{ fontSize: '13px', color: 'error.main' }}>Não foi possível carregar os planos.</Typography>
        )}

        {status === 'ready' && visiblePlans.length === 0 && (
          <Card elevation={0} sx={{ p: '20px' }}>
            <Typography sx={{ fontSize: '13px', mb: 1.5 }}>
              Os planos estarão disponíveis em breve. Fale com um consultor para conhecer as condições.
            </Typography>
            <Button
              variant="contained"
              size="small"
              component="a"
              href={WHATSAPP_CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Falar com consultor
            </Button>
          </Card>
        )}

        {status === 'ready' && visiblePlans.length > 0 && isMobileShell && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: 14, lineHeight: 1.4 }}>
            {visiblePlans.map((plan) => (
              <PlanCardMobile key={plan.id} plan={plan} tier={tiers.get(plan.id)} currentCents={currentCents} hasPlan={Boolean(currentPlan)} />
            ))}
          </Box>
        )}

        {status === 'ready' && visiblePlans.length > 0 && !isMobileShell && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(260px, 1fr))' }, gap: 2 }}>
            {visiblePlans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} tier={tiers.get(plan.id)} currentCents={currentCents} hasPlan={Boolean(currentPlan)} />
            ))}
          </Box>
        )}
      </Box>
    </PageScaffold>
  );
}
